import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
  TextField,
  IconButton,
  Snackbar,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import axios from 'axios';
import moment from 'moment';

function arrayToDate(arr) {
  if (!Array.isArray(arr) || arr.length < 6) return null;
  return new Date(
    arr[0],
    arr[1] - 1,
    arr[2],
    arr[3],
    arr[4],
    arr[5],
    arr[6] ? Math.floor(arr[6] / 1000000) : 0
  );
}

function formatHistoryDate(value) {
  if (!value) return '-';
  if (Array.isArray(value)) {
    const date = arrayToDate(value);
    return date ? moment(date).format('MMM D, YYYY h:mm A') : '-';
  }
  return moment(value).format('MMM D, YYYY h:mm A');
}

const historyColSx = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center'
};

/**
 * Modal: list tools for one MCP provider, edit instructions, toggle enabled, revert to admin default.
 */
export default function AgentMcpToolInstructionsModal({
  open,
  onClose,
  agentId,
  connection,
  attached
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [instructionsDraft, setInstructionsDraft] = useState('');
  const [whenInstructionsDraft, setWhenInstructionsDraft] = useState('');
  const [activeDraft, setActiveDraft] = useState(true);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [revertConfirmOpen, setRevertConfirmOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  const providerKey = connection?.providerKey;
  const title = connection?.displayName || providerKey || 'Tools';

  const loadRows = useCallback(async () => {
    if (!agentId || !providerKey || !attached) return;
    setLoading(true);
    try {
      const { data } = await axios.post('/agents/listMcpToolInstructions', {}, {
        params: { agentId, providerKey }
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to load tools';
      setSnackbar({ open: true, message: msg, severity: 'error' });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [agentId, providerKey, attached]);

  useEffect(() => {
    if (open && attached && providerKey) {
      loadRows();
    } else if (!open) {
      setRows([]);
      setEditRow(null);
      setRevertConfirmOpen(false);
    }
  }, [open, attached, providerKey, loadRows]);

  useEffect(() => {
    if (!editRow?.id || !agentId) {
      setHistoryRows([]);
      setHistoryError(null);
      setHistoryLoading(false);
      return;
    }

    let cancelled = false;
    setHistoryLoading(true);
    setHistoryError(null);

    axios.post('/agents/mcp-tool-instructions/history', null, {
      params: { id: editRow.id, agentId }
    })
      .then((res) => {
        if (!cancelled) {
          setHistoryRows(Array.isArray(res.data) ? res.data : []);
          setHistoryLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHistoryError('Failed to load history.');
          setHistoryRows([]);
          setHistoryLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [editRow?.id, agentId, historyRefreshKey]);

  const handleOpenHistoryDialog = (row, versionNumber) => {
    setSelectedHistory({ ...row, versionNumber });
    setHistoryDialogOpen(true);
  };

  const handleCloseHistoryDialog = () => {
    setHistoryDialogOpen(false);
    setSelectedHistory(null);
  };

  const handleUseHistoryVersion = () => {
    if (!selectedHistory) return;
    setInstructionsDraft(selectedHistory.instructions ?? '');
    setWhenInstructionsDraft(selectedHistory.whenInstructions ?? '');
    setActiveDraft(selectedHistory.isActive !== false);
    setFormError('');
    handleCloseHistoryDialog();
    setSnackbar({
      open: true,
      message: 'History loaded into the editor. Click Save to persist your changes.',
      severity: 'info'
    });
  };

  const renderHistoryContent = () => {
    if (historyLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }
    if (historyError) {
      return (
        <Typography variant="body2" color="error">
          {historyError}
        </Typography>
      );
    }
    if (historyRows.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No history available.
        </Typography>
      );
    }

    return (
      <Box sx={{ maxHeight: 152, overflowY: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 0,
            py: 0.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'action.hover'
          }}
        >
          <Typography variant="caption" fontWeight="bold" sx={historyColSx}>
            Version
          </Typography>
          <Typography variant="caption" fontWeight="bold" sx={historyColSx}>
            Date
          </Typography>
          <Typography variant="caption" fontWeight="bold" sx={historyColSx}>
            User
          </Typography>
          <Box sx={{ ...historyColSx, justifyContent: 'flex-end' }} />
        </Box>
        <List dense disablePadding>
          {historyRows.map((row, index) => {
            const versionNumber = historyRows.length - index;
            const formattedDate = formatHistoryDate(row.historyDateCreated);
            const userName = row.updatedBy || '-';
            return (
              <ListItem
                key={row.historyId}
                sx={{
                  px: 0,
                  py: 0.5,
                  minHeight: 40,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Typography variant="caption" sx={historyColSx}>
                  #{versionNumber}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ...historyColSx, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={formattedDate}
                >
                  {formattedDate}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ...historyColSx, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={userName}
                >
                  {userName}
                </Typography>
                <Box sx={{ ...historyColSx, justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    endIcon={<NavigateNextIcon />}
                    onClick={() => handleOpenHistoryDialog(row, versionNumber)}
                    sx={{ flexShrink: 0, px: 1, py: 0.25, fontSize: '0.7rem' }}
                  >
                    View
                  </Button>
                </Box>
              </ListItem>
            );
          })}
        </List>
      </Box>
    );
  };

  const handleSaveEdit = async () => {
    const trimmed = (instructionsDraft ?? '').trim();
    if (!trimmed) {
      setFormError('Instructions cannot be empty.');
      return;
    }
    setFormError('');
    setSaving(true);
    try {
      const { data } = await axios.put(
        `/agents/mcp-tool-instructions/${editRow.id}`,
        {
          instructions: trimmed,
          whenInstructions: (whenInstructionsDraft ?? '').trim() || null,
          isActive: activeDraft
        },
        { params: { agentId } }
      );
      setRows((prev) => prev.map((r) => (r.id === data.id ? data : r)));
      setSnackbar({ open: true, message: 'Saved.', severity: 'success' });
      setEditRow(null);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Save failed';
      setSnackbar({ open: true, message: typeof msg === 'string' ? msg : 'Save failed', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleRow = async (row, nextActive) => {
    const trimmed = (row.instructions ?? '').trim();
    if (!trimmed) {
      setSnackbar({ open: true, message: 'Instructions cannot be empty.', severity: 'error' });
      return;
    }
    try {
      const { data } = await axios.put(
        `/agents/mcp-tool-instructions/${row.id}`,
        {
          instructions: trimmed,
          whenInstructions: (row.whenInstructions ?? '').trim() || null,
          isActive: nextActive
        },
        { params: { agentId } }
      );
      setRows((prev) => prev.map((r) => (r.id === data.id ? data : r)));
      setSnackbar({ open: true, message: 'Saved.', severity: 'success' });
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Save failed';
      setSnackbar({ open: true, message: typeof msg === 'string' ? msg : 'Save failed', severity: 'error' });
    }
  };

  const handleRevert = async () => {
    if (!editRow) return;
    setSaving(true);
    try {
      const { data } = await axios.post(
        `/agents/mcp-tool-instructions/${editRow.id}/revert-to-default`,
        null,
        { params: { agentId } }
      );
      setRows((prev) => prev.map((r) => (r.id === data.id ? data : r)));
      setInstructionsDraft(data.instructions ?? '');
      setWhenInstructionsDraft(data.whenInstructions ?? '');
      setActiveDraft(!!data.isActive);
      setSnackbar({ open: true, message: 'Reverted to default instructions.', severity: 'success' });
      setRevertConfirmOpen(false);
      setHistoryRefreshKey((k) => k + 1);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Revert failed';
      setSnackbar({ open: true, message: typeof msg === 'string' ? msg : 'Revert failed', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (row) => {
    setEditRow(row);
    setInstructionsDraft(row.instructions ?? '');
    setWhenInstructionsDraft(row.whenInstructions ?? '');
    setActiveDraft(row.isActive !== false);
    setFormError('');
    setHistoryExpanded(false);
  };

  const largeDialogPaperProps = {
    sx: {
      width: 'min(96vw, 1200px)',
      maxWidth: 'none',
      maxHeight: '90vh',
    },
  };

  const editDialogPaperProps = {
    sx: {
      width: 'min(96vw, 1200px)',
      maxWidth: 'none',
      height: '90vh',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => !saving && onClose()}
        fullWidth
        maxWidth={false}
        PaperProps={largeDialogPaperProps}
      >
        <DialogTitle>Configure — {title}</DialogTitle>
        <DialogContent sx={{ overflow: 'auto' }}>
          {!attached ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Attach this integration to the agent before updating tool instructions.
            </Typography>
          ) : loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tool</TableCell>
                    <TableCell align="center" width={120}>Enabled</TableCell>
                    <TableCell align="center" width={72}>Edit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="body2" color="text.secondary">
                          No tool rows yet. Try saving the agent or re-open after attaching.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.toolName}</TableCell>
                        <TableCell align="center">
                          <Switch
                            size="small"
                            checked={row.isActive !== false}
                            onChange={() => handleToggleRow(row, !(row.isActive !== false))}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" color="primary" onClick={() => openEdit(row)} aria-label="Edit">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!editRow}
        onClose={() => !saving && setEditRow(null)}
        fullWidth
        maxWidth={false}
        PaperProps={editDialogPaperProps}
        sx={{
          '& .MuiDialog-paper': {
            display: 'flex',
            flexDirection: 'column',
            height: '90vh',
            maxHeight: '90vh',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ flexShrink: 0, pb: 1 }}>Edit tool instructions</DialogTitle>
        <Box
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            px: 3,
            py: 1,
            borderTop: 1,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          {editRow && (
            <>
              <Typography variant="subtitle2" color="text.secondary">Tool</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>{editRow.toolName}</Typography>
              <TextField
                label="When (intent — merged into agent instructions)"
                value={whenInstructionsDraft}
                onChange={(e) => {
                  setWhenInstructionsDraft(e.target.value);
                  setFormError('');
                }}
                fullWidth
                multiline
                minRows={3}
                maxRows={8}
                margin="dense"
                helperText="Optional. Guides when to steer the conversation toward this tool."
                sx={{
                  '& .MuiInputBase-root': { alignItems: 'flex-start' },
                  '& textarea': {
                    maxHeight: 160,
                    overflowY: 'auto !important',
                  },
                }}
              />
              <TextField
                label="How (execution — on tool definition)"
                value={instructionsDraft}
                onChange={(e) => {
                  setInstructionsDraft(e.target.value);
                  setFormError('');
                }}
                fullWidth
                multiline
                minRows={3}
                maxRows={8}
                margin="dense"
                error={!!formError}
                helperText={formError || 'Required. Cannot be empty or whitespace-only.'}
                sx={{
                  mt: 1,
                  '& .MuiInputBase-root': { alignItems: 'flex-start' },
                  '& textarea': {
                    maxHeight: 200,
                    overflowY: 'auto !important',
                  },
                }}
              />
              <FormControlLabel
                sx={{ mt: 0.5 }}
                control={(
                  <Switch
                    checked={activeDraft}
                    onChange={(e) => setActiveDraft(e.target.checked)}
                  />
                )}
                label="Tool enabled"
              />
              <Accordion
                disableGutters
                elevation={0}
                sx={{
                  mt: 1,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  '&:before': { display: 'none' },
                }}
                expanded={historyExpanded}
                onChange={() => setHistoryExpanded((prev) => !prev)}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, px: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <HistoryIcon color="action" sx={{ mr: 1, fontSize: 20 }} />
                    <Typography variant="subtitle2">
                      History{historyRows.length > 0 ? ` (${historyRows.length})` : ''}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
                  {renderHistoryContent()}
                </AccordionDetails>
              </Accordion>
            </>
          )}
        </Box>
        <DialogActions
          sx={{
            flexShrink: 0,
            flexWrap: 'wrap',
            gap: 1,
            px: 3,
            py: 1.5,
            m: 0,
            bgcolor: 'background.paper',
          }}
        >
          <Button onClick={() => setEditRow(null)} disabled={saving}>Cancel</Button>
          <Button onClick={() => setRevertConfirmOpen(true)} disabled={saving} color="warning" variant="outlined">
            Revert to default
          </Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={historyDialogOpen}
        onClose={handleCloseHistoryDialog}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          {selectedHistory ? `Version ${selectedHistory.versionNumber}` : 'History'}
          {selectedHistory && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {formatHistoryDate(selectedHistory.historyDateCreated)}
              {selectedHistory.updatedBy ? ` · ${selectedHistory.updatedBy}` : ''}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            When (intent)
          </Typography>
          <Box sx={{ maxHeight: 200, overflowY: 'auto', mb: 2 }}>
            <Typography
              variant="body2"
              component="pre"
              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', m: 0 }}
            >
              {selectedHistory?.whenInstructions || '(empty)'}
            </Typography>
          </Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            How (execution)
          </Typography>
          <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
            <Typography
              variant="body2"
              component="pre"
              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', m: 0 }}
            >
              {selectedHistory?.instructions || '(empty)'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHistoryDialog}>Close</Button>
          <Button variant="contained" onClick={handleUseHistoryVersion}>
            Use this version
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={revertConfirmOpen} onClose={() => !saving && setRevertConfirmOpen(false)}>
        <DialogTitle>Revert to default?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will replace your custom instructions for this tool with the current default from the admin catalog.
            Continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevertConfirmOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleRevert} color="warning" variant="contained" disabled={saving}>
            {saving ? 'Reverting…' : 'Revert'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
